import { requestEmailConfirmation } from 'api/services/emailConfirmService'
import { hasListingOwnerIntent } from 'api/utils/pendingCreateAdFlow'
import AuthPageShell from 'components/auth/AuthPageShell'
import PageLoading from 'components/common/loading/PageLoading'
import { useAuth } from 'pages/authenticated/AuthContext'
import { usePermissions } from 'pages/authenticated/PermissionsContext'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const VerifyEmailPending = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { user, isAuthenticated } = useAuth()
  const { email, emailValidated, hasListing, loading, refresh, role } =
    usePermissions()
  const [resending, setResending] = useState(false)
  const [checking, setChecking] = useState(false)

  const nextPath =
    typeof (location.state as { next?: string } | null)?.next === 'string'
      ? (location.state as { next: string }).next
      : '/account'

  const displayEmail =
    email ||
    (user && typeof user === 'object' && typeof user.email === 'string'
      ? user.email
      : '')

  const isPublisher =
    role === 'owner' || hasListing || hasListingOwnerIntent()

  const goNext = async () => {
    await queryClient.invalidateQueries(['myProperties'])
    await queryClient.invalidateQueries(['ownerDashboardSummary'])
    navigate(nextPath || '/account', { replace: true })
  }

  // Do not auto-send here: registration already sends the welcome/confirm email.
  // Auto-send was regenerating the token and invalidating the first link.

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace state={{ from: location }} />
  }

  if (loading) {
    return <PageLoading />
  }

  // Only leave this screen when email is confirmed — never bounce publishers
  // back to /account just because role briefly looks like tenant (that showed
  // an empty Mes annonces list instead of this validation UI).
  if (emailValidated) {
    return <Navigate to={nextPath || '/account'} replace />
  }

  // Seekers with no listing intent do not need this gate.
  if (!isPublisher) {
    return <Navigate to={nextPath || '/account'} replace />
  }

  const handleResend = async () => {
    setResending(true)
    try {
      // force:true only rotates an unconfirmed pending token — confirmed
      // emails are never wiped (API returns alreadyConfirmed).
      const result = await requestEmailConfirmation({ force: true })
      if (result.alreadyConfirmed) {
        await refresh()
        toast.success(String(t('auth.verifyEmail.alreadyConfirmed')))
        await goNext()
        return
      }
      toast.success(String(t('auth.verifyEmail.resendSuccess')))
    } catch {
      toast.error(String(t('auth.verifyEmail.resendFailed')))
    } finally {
      setResending(false)
    }
  }

  const handleIConfirmed = async () => {
    setChecking(true)
    try {
      const payload = await refresh()
      if (payload?.emailValidated) {
        toast.success(String(t('auth.verifyEmail.alreadyConfirmed')))
        await goNext()
        return
      }
      toast.info(String(t('auth.verifyEmail.stillPending')))
    } finally {
      setChecking(false)
    }
  }

  return (
    <AuthPageShell
      title={t('auth.verifyEmail.title')}
      subtitle={t('auth.verifyEmail.subtitle')}
    >
      <div className="flex flex-col items-center gap-5 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#DCE8F8] text-imovPrimary">
          <svg
            viewBox="0 0 24 24"
            className="h-8 w-8"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>

        {displayEmail ? (
          <p className="text-sm text-gray-600">
            {t('auth.verifyEmail.sentTo')}{' '}
            <span className="font-semibold text-imovSecondary">{displayEmail}</span>
          </p>
        ) : null}

        <p className="max-w-sm text-sm leading-relaxed text-gray-500">
          {t('auth.verifyEmail.body')}
        </p>

        <button
          type="button"
          onClick={() => void handleResend()}
          disabled={resending}
          className="inline-flex min-h-[48px] w-full items-center justify-center rounded-xl bg-imovPrimary px-5 py-3 text-sm font-semibold text-white transition hover:bg-imovPrimaryDark disabled:opacity-50"
        >
          {resending ? t('auth.verifyEmail.resending') : t('auth.verifyEmail.resend')}
        </button>

        <button
          type="button"
          onClick={() => void handleIConfirmed()}
          disabled={checking}
          className="inline-flex min-h-[48px] w-full items-center justify-center rounded-xl border border-gray-300 bg-white px-5 py-3 text-sm font-semibold text-imovSecondary transition hover:bg-gray-50 disabled:opacity-50"
        >
          {checking ? t('auth.verifyEmail.checking') : t('auth.verifyEmail.iConfirmed')}
        </button>

        <Link
          to="/"
          className="text-sm font-medium text-gray-500 transition hover:text-imovSecondary"
        >
          {t('auth.verifyEmail.backHome')}
        </Link>
      </div>
    </AuthPageShell>
  )
}

export default VerifyEmailPending
